import clsx from "clsx";
import type { ComponentProps, ReactNode } from "react";
import { useCallback } from "react";

import type { ColorType } from "@shared/types";
import { BrandIcon } from "@shared/ui/brand-icon";
import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import type { IconNameType } from "@shared/ui/icon";
import { useScroll } from "@shared/ui/scroll/scroll";
import { Body } from "@shared/ui/typography";

import s from "./point-contact-content.module.scss";

export type PointContactItem = {
  key: string;
  title: string;
  iconName: IconNameType;
  iconColor: ColorType;
  content: ReactNode;
};

export type PointContactContentProps = ComponentProps<"div"> & {
  className?: string;
  items: PointContactItem[];
  siteUrl?: string | null;
  mapAnchorId?: string;
  isTablet?: boolean;
};

export const PointContactContent = (props: PointContactContentProps) => {
  const { className, items, siteUrl, mapAnchorId, isTablet } = props;
  const scroll = useScroll();

  const handleMapClick = useCallback(() => {
    if (!scroll || !mapAnchorId) return;
    scroll.scrollTo(`#${mapAnchorId}`, { offset: -100 });
  }, [mapAnchorId, scroll]);

  const visibleItems = (items ?? []).filter((item) => Boolean(item?.content));

  return (
    <div
      className={clsx(s.root, className, { [s.top]: isTablet })}
      data-inner-element="sections"
    >
      {visibleItems.length > 0 && (
        <div className={s.sections} data-inner-element="list">
          {visibleItems.map((item) =>
            item ? (
              <div
                key={item?.key ?? item?.title ?? Math.random()}
                className={s.item}
              >
                <BrandIcon
                  size={16}
                  name={item.iconName}
                  fillColor={item.iconColor}
                  strokeColor="milk"
                  className={s.icon}
                />
                <div className={s.itemContent}>
                  <Body
                    size="uppercase-small"
                    weight="semiBold"
                    className={s.subtitle}
                  >
                    {item.title}
                  </Body>
                  <div className={s.value}>{item.content}</div>
                </div>
              </div>
            ) : null,
          )}
        </div>
      )}
      {(siteUrl || mapAnchorId) && (
        <div className={s.buttons} data-inner-element="buttons">
          {siteUrl && (
            <ButtonWithIcon
              variant="secondary-dark"
              href={siteUrl}
              target="_blank"
              rel="noopener noreferrer"
              iconName="arrow"
              iconFillColor="cadmium"
              iconStrokeColor="milk"
            >
              Перейти на сайт
            </ButtonWithIcon>
          )}
          {mapAnchorId && (
            <ButtonWithIcon
              variant="primary-dark"
              onClick={handleMapClick}
              iconName="arrow"
              iconFillColor="cadmium"
              iconStrokeColor="milk"
            >
              Показать на карте
            </ButtonWithIcon>
          )}
        </div>
      )}
    </div>
  );
};

PointContactContent.displayName = "PointContactContent";
